import { type FC, type MouseEvent, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilValue, useSetRecoilState } from 'recoil'

import { edgeResizePreviewAtomFamily } from '@/atoms/itemControlAtoms'
import { itemSelector } from '@/atoms/_editorAtoms'

import {
  ClassicBottomEdgeHandle,
  ClassicRightEdgeHandle,
} from '@/components/page/Items/ItemControlOverlay/classic/ClassicItemControlOverlay.styled'
import { Tooltip } from '@/components/global/Tooltip/Tooltip'

import { useItemResize } from '@/hooks/editor/useItemResize'

type ClassicEdgeResizeControlsProps = {
  itemId: string
  itemsLocked: boolean
  zoom: number
  currentWidth: number
  currentHeight: number
}

/**
 * Edge resize handles for image items, identical to staging's; an editor
 * text box's pills are `controls/EdgeResizeControls.tsx`.
 * - Right edge: drag horizontally to change width only
 * - Bottom edge: drag vertically to change height (or proportionally)
 *
 * Uses Option 1 pattern: Updates internal dimensions during drag via
 * edgeResizePreviewAtomFamily, then commits to Recoil on mouse up.
 * This matches the corner resize behavior where the border moves
 * during drag and the image updates on release.
 */
export const ClassicEdgeResizeControls: FC<ClassicEdgeResizeControlsProps> = ({
  itemId,
  itemsLocked,
  zoom,
  currentWidth,
  currentHeight,
}) => {
  const { t } = useTranslation()
  const { setWidthSize, setHeightSize, setSize } = useItemResize(itemId)
  const setEdgeResizePreview = useSetRecoilState(
    edgeResizePreviewAtomFamily(itemId),
  )

  // Check if height can be changed independently
  // When maintainAspectRatio is OFF (false), height auto-adjusts based on width
  const maintainAspectRatio = useRecoilValue(
    itemSelector({ id: itemId, path: 'maintainAspectRatio' }),
  )
  const canChangeHeightIndependently = !!maintainAspectRatio

  // Scale factor for zoom, clamped to prevent extremes
  const scale = Math.min(2, Math.max(0.5, 100 / zoom))

  // Track cleanup functions for event listeners to handle unmount during drag
  const cleanupRef = useRef<(() => void) | null>(null)

  // Cleanup on unmount - remove any lingering event listeners
  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current()
        cleanupRef.current = null
      }
    }
  }, [])

  const handleRightMouseDown = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      const startX = e.clientX
      const startWidth = currentWidth
      const startHeight = currentHeight
      const zoomFactor = zoom / 100
      const aspectRatio = startHeight / startWidth

      const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
        const deltaX = (moveEvent.clientX - startX) / zoomFactor
        const newWidth = Math.max(20, startWidth + deltaX)

        if (canChangeHeightIndependently) {
          // maintainAspectRatio is ON: change width only
          setEdgeResizePreview({ width: newWidth, height: startHeight })
        } else {
          // maintainAspectRatio is OFF: proportional resize (height auto-adjusts)
          const newHeight = Math.max(20, newWidth * aspectRatio)
          setEdgeResizePreview({ width: newWidth, height: newHeight })
        }
      }

      const handleMouseUp = (upEvent: globalThis.MouseEvent) => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        cleanupRef.current = null

        // Calculate final width
        const deltaX = (upEvent.clientX - startX) / zoomFactor
        const newWidth = Math.max(20, startWidth + deltaX)

        // Clear preview and commit actual size
        setEdgeResizePreview(null)

        if (canChangeHeightIndependently) {
          // maintainAspectRatio is ON: only width changes
          setWidthSize(newWidth)
        } else {
          // maintainAspectRatio is OFF: commit both width and height
          const newHeight = Math.max(20, newWidth * aspectRatio)
          setSize(newWidth, newHeight)
        }
      }

      // Store cleanup function in ref for unmount handling
      cleanupRef.current = () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        setEdgeResizePreview(null)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [
      currentWidth,
      currentHeight,
      zoom,
      setWidthSize,
      setSize,
      setEdgeResizePreview,
      canChangeHeightIndependently,
    ],
  )

  const handleBottomMouseDown = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      const startY = e.clientY
      const startHeight = currentHeight
      const startWidth = currentWidth
      const zoomFactor = zoom / 100
      const aspectRatio = startWidth / startHeight

      const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
        const deltaY = (moveEvent.clientY - startY) / zoomFactor
        const newHeight = Math.max(20, startHeight + deltaY)

        if (canChangeHeightIndependently) {
          // maintainAspectRatio is ON: change height only
          setEdgeResizePreview({ width: startWidth, height: newHeight })
        } else {
          // maintainAspectRatio is OFF: proportional resize
          const newWidth = Math.max(20, newHeight * aspectRatio)
          setEdgeResizePreview({ width: newWidth, height: newHeight })
        }
      }

      const handleMouseUp = (upEvent: globalThis.MouseEvent) => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        cleanupRef.current = null

        // Calculate final dimensions
        const deltaY = (upEvent.clientY - startY) / zoomFactor
        const newHeight = Math.max(20, startHeight + deltaY)

        // Clear preview and commit actual size
        setEdgeResizePreview(null)

        if (canChangeHeightIndependently) {
          setHeightSize(newHeight)
        } else {
          const newWidth = Math.max(20, newHeight * aspectRatio)
          setSize(newWidth, newHeight)
        }
      }

      // Store cleanup function in ref for unmount handling
      cleanupRef.current = () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        setEdgeResizePreview(null)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [
      currentHeight,
      currentWidth,
      zoom,
      setHeightSize,
      setSize,
      canChangeHeightIndependently,
      setEdgeResizePreview,
    ],
  )

  if (itemsLocked) return null

  return (
    <>
      {/* Right edge handle - changes width (proportionally when maintainAspectRatio is OFF) */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          right: 0,
          transform: `translateY(-50%) translateX(50%) scale(${scale})`,
          transformOrigin: 'center center',
          pointerEvents: 'auto',
        }}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <Tooltip
          text={
            canChangeHeightIndependently
              ? t('itemControls.resizeWidth', 'Resize width')
              : t('itemControls.resizeProportional', 'Resize proportionally')
          }
          placement="right"
          offsetX={8}
        >
          <ClassicRightEdgeHandle
            onMouseDown={handleRightMouseDown}
            aria-label={
              canChangeHeightIndependently
                ? t('itemControls.resizeWidth', 'Resize width')
                : t('itemControls.resizeProportional', 'Resize proportionally')
            }
            data-testid="overlay-right-edge-handle"
          />
        </Tooltip>
      </div>

      {/* Bottom edge handle - changes height (proportionally when maintainAspectRatio is OFF) */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: '50%',
          transform: `translateX(-50%) translateY(50%) scale(${scale})`,
          transformOrigin: 'center center',
          pointerEvents: 'auto',
        }}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        <Tooltip
          text={
            canChangeHeightIndependently
              ? t('itemControls.resizeHeight', 'Resize height')
              : t('itemControls.resizeProportional', 'Resize proportionally')
          }
          placement="bottom"
          offsetY={8}
        >
          <ClassicBottomEdgeHandle
            onMouseDown={handleBottomMouseDown}
            aria-label={
              canChangeHeightIndependently
                ? t('itemControls.resizeHeight', 'Resize height')
                : t('itemControls.resizeProportional', 'Resize proportionally')
            }
            data-testid="overlay-bottom-edge-handle"
          />
        </Tooltip>
      </div>
    </>
  )
}
